<template>
  <div v-if="leave" class="leave-details-panel">
    <div class="ld-hero" :class="statusTone">
      <div class="ld-hero-content">
        <span class="ld-status-pill">{{ normalizedStatus }}</span>
        <h3 class="ld-title">{{ leave.leaveTypeName || "Leave request" }}</h3>
        <p class="ld-subtitle">{{ statusMessage }}</p>
      </div>
      <div class="ld-hero-stats">
        <div class="ld-stat">
          <span class="ld-stat-label">Duration</span>
          <strong class="ld-stat-value">{{ leave.totalDays || 0 }}<small>days</small></strong>
        </div>
        <div v-if="leave.id" class="ld-stat">
          <span class="ld-stat-label">Request ID</span>
          <strong class="ld-stat-value">#{{ leave.id }}</strong>
        </div>
      </div>
    </div>

    <div v-if="showEmployee && leave.names" class="ld-employee">
      <div class="ld-avatar">{{ employeeInitials }}</div>
      <div>
        <p class="ld-employee-name">{{ leave.names }}</p>
        <p class="ld-employee-meta">{{ leave.departmentName || "Department not set" }}</p>
      </div>
    </div>

    <div class="ld-bento">
      <div class="ld-bento-card">
        <i class="bx bx-calendar-event ld-bento-icon"></i>
        <span class="ld-bento-label">Start date</span>
        <span class="ld-bento-value">{{ formatDate(leave.startDate) }}</span>
      </div>
      <div class="ld-bento-card">
        <i class="bx bx-calendar-check ld-bento-icon ld-icon-end"></i>
        <span class="ld-bento-label">End date</span>
        <span class="ld-bento-value">{{ formatDate(leave.endDate) }}</span>
      </div>
      <div class="ld-bento-card">
        <i class="bx bx-time-five ld-bento-icon"></i>
        <span class="ld-bento-label">Applied on</span>
        <span class="ld-bento-value">{{ formatDate(leave.createdAt) }}</span>
      </div>
      <div class="ld-bento-card">
        <i class="bx bx-category ld-bento-icon"></i>
        <span class="ld-bento-label">Leave type</span>
        <span class="ld-bento-value">{{ leave.leaveTypeName || "—" }}</span>
      </div>
    </div>

    <div v-if="leave.reason" class="ld-section">
      <h4 class="ld-section-title"><i class="bx bx-message-detail"></i> Reason</h4>
      <p class="ld-reason-text">{{ leave.reason }}</p>
    </div>

    <div v-if="leave.contactNumber || leave.alternateContact" class="ld-section">
      <h4 class="ld-section-title"><i class="bx bx-phone"></i> Contact while away</h4>
      <div class="ld-info-grid">
        <div v-if="leave.contactNumber" class="ld-info-item">
          <span class="ld-info-label">Phone</span>
          <span class="ld-info-value">{{ leave.contactNumber }}</span>
        </div>
        <div v-if="leave.alternateContact" class="ld-info-item">
          <span class="ld-info-label">Alternate contact</span>
          <span class="ld-info-value">{{ leave.alternateContact }}</span>
        </div>
      </div>
    </div>

    <div v-if="leave.departmentName || leave.managerName || leave.approvedBy || leave.rejectedBy" class="ld-section">
      <h4 class="ld-section-title"><i class="bx bx-buildings"></i> Approval</h4>
      <div class="ld-info-grid">
        <div v-if="leave.departmentName" class="ld-info-item">
          <span class="ld-info-label">Department</span>
          <span class="ld-info-value">{{ leave.departmentName }}</span>
        </div>
        <div v-if="leave.managerName" class="ld-info-item">
          <span class="ld-info-label">Manager</span>
          <span class="ld-info-value">{{ leave.managerName }}</span>
        </div>
        <div v-if="leave.approvedBy" class="ld-info-item ld-info-success">
          <span class="ld-info-label">Approved by</span>
          <span class="ld-info-value">{{ leave.approvedBy }}</span>
          <span v-if="leave.approvalDate" class="ld-info-hint">{{ formatDateTime(leave.approvalDate) }}</span>
        </div>
        <div v-if="leave.rejectedBy" class="ld-info-item ld-info-danger">
          <span class="ld-info-label">Rejected by</span>
          <span class="ld-info-value">{{ leave.rejectedBy }}</span>
          <span v-if="leave.rejectionDate" class="ld-info-hint">{{ formatDateTime(leave.rejectionDate) }}</span>
        </div>
      </div>
    </div>

    <div v-if="leave.rejectionReason" class="ld-alert ld-alert-danger">
      <i class="bx bx-error-circle"></i>
      <div>
        <strong>Rejection reason</strong>
        <p>{{ leave.rejectionReason }}</p>
      </div>
    </div>

    <div v-if="leave.documents" class="ld-section">
      <h4 class="ld-section-title"><i class="bx bx-paperclip"></i> Attachment</h4>
      <button type="button" class="ld-doc-btn" @click="openDocument">
        <i class="bx bx-file"></i>
        <span>View supporting document</span>
        <i class="bx bx-link-external"></i>
      </button>
    </div>

    <div class="ld-section">
      <h4 class="ld-section-title"><i class="bx bx-git-commit"></i> Activity</h4>
      <ul class="ld-timeline">
        <li class="ld-timeline-item">
          <span class="ld-dot ld-dot-primary"></span>
          <div class="ld-timeline-card">
            <strong>Request submitted</strong>
            <span>{{ formatDateTime(leave.createdAt) }}</span>
          </div>
        </li>
        <li v-if="leave.approvalDate" class="ld-timeline-item">
          <span class="ld-dot ld-dot-success"></span>
          <div class="ld-timeline-card">
            <strong>Approved</strong>
            <span>{{ formatDateTime(leave.approvalDate) }}</span>
            <small v-if="leave.approvedBy">by {{ leave.approvedBy }}</small>
          </div>
        </li>
        <li v-if="leave.rejectionDate" class="ld-timeline-item">
          <span class="ld-dot ld-dot-danger"></span>
          <div class="ld-timeline-card">
            <strong>Rejected</strong>
            <span>{{ formatDateTime(leave.rejectionDate) }}</span>
            <small v-if="leave.rejectedBy">by {{ leave.rejectedBy }}</small>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { uploads } from "@/api";

export default {
  name: "LeaveDetailsPanel",
  props: {
    leave: { type: Object, default: null },
    showEmployee: { type: Boolean, default: false }
  },
  computed: {
    normalizedStatus() {
      const s = String(this.leave?.status || "Unknown").toLowerCase();
      if (s === "pending") return "Pending";
      if (s === "approved") return "Approved";
      if (s === "rejected") return "Rejected";
      if (s === "cancelled") return "Cancelled";
      return this.leave?.status || "Unknown";
    },
    statusTone() {
      const s = this.normalizedStatus.toLowerCase();
      if (s === "approved") return "ld-hero--approved";
      if (s === "rejected") return "ld-hero--rejected";
      if (s === "cancelled") return "ld-hero--cancelled";
      if (s === "pending") return "ld-hero--pending";
      return "ld-hero--default";
    },
    statusMessage() {
      const s = this.normalizedStatus.toLowerCase();
      if (s === "pending") return "Awaiting manager or HR approval";
      if (s === "approved") return "This leave request has been approved";
      if (s === "rejected") return "This leave request was not approved";
      if (s === "cancelled") return "This request was cancelled";
      return "Leave application summary";
    },
    employeeInitials() {
      const name = this.leave?.names || "";
      if (!name) return "—";
      return name
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return "—";
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
    },
    formatDateTime(datetime) {
      if (!datetime) return "—";
      const d = new Date(datetime);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    openDocument() {
      if (!this.leave?.documents) return;
      window.open(uploads + this.leave.documents, "_blank");
    }
  }
};
</script>

<style scoped>
.leave-details-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ld-hero {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%);
}

.ld-hero--pending {
  background: linear-gradient(135deg, #d97706 0%, #f59e0b 55%, #fbbf24 100%);
}

.ld-hero--approved {
  background: linear-gradient(135deg, #059669 0%, #10b981 55%, #34d399 100%);
}

.ld-hero--rejected {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 55%, #f87171 100%);
}

.ld-hero--cancelled {
  background: linear-gradient(135deg, #475569 0%, #64748b 55%, #94a3b8 100%);
}

.ld-status-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(4px);
  margin-bottom: 8px;
}

.ld-title {
  margin: 0 0 6px;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.25;
}

.ld-subtitle {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.92;
}

.ld-hero-stats {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.ld-stat {
  min-width: 88px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(6px);
  text-align: center;
}

.ld-stat-label {
  display: block;
  font-size: 11px;
  opacity: 0.85;
  margin-bottom: 4px;
}

.ld-stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.1;
}

.ld-stat-value small {
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 3px;
  opacity: 0.9;
}

.ld-employee {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.ld-avatar {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1d4ed8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
  flex-shrink: 0;
}

.ld-employee-name {
  margin: 0;
  font-weight: 700;
  color: #0f172a;
  font-size: 1rem;
}

.ld-employee-meta {
  margin: 2px 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.ld-bento {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ld-bento-card {
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.ld-bento-icon {
  font-size: 1.25rem;
  color: #4f46e5;
  margin-bottom: 8px;
  display: block;
}

.ld-icon-end {
  color: #ef4444;
}

.ld-bento-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #94a3b8;
  margin-bottom: 4px;
}

.ld-bento-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
}

.ld-section {
  padding: 4px 0;
}

.ld-section-title {
  margin: 0 0 12px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ld-section-title i {
  font-size: 1.1rem;
  color: #4f46e5;
}

.ld-reason-text {
  margin: 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  line-height: 1.55;
  font-size: 0.95rem;
}

.ld-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ld-info-item {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.ld-info-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}

.ld-info-value {
  display: block;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.92rem;
}

.ld-info-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.78rem;
  color: #64748b;
}

.ld-info-success {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.ld-info-danger {
  border-color: #fecaca;
  background: #fef2f2;
}

.ld-alert {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  align-items: flex-start;
}

.ld-alert i {
  font-size: 1.35rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.ld-alert strong {
  display: block;
  margin-bottom: 4px;
  font-size: 0.9rem;
}

.ld-alert p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
}

.ld-alert-danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.ld-doc-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px dashed #c7d2fe;
  background: #eef2ff;
  color: #3730a3;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.ld-doc-btn:hover {
  background: #e0e7ff;
  border-color: #a5b4fc;
}

.ld-doc-btn span {
  flex: 1;
  text-align: left;
}

.ld-timeline {
  list-style: none;
  margin: 0;
  padding: 4px 0 4px 8px;
  position: relative;
}

.ld-timeline::before {
  content: "";
  position: absolute;
  left: 15px;
  top: 8px;
  bottom: 8px;
  width: 2px;
  background: linear-gradient(180deg, #c7d2fe, #e2e8f0);
  border-radius: 2px;
}

.ld-timeline-item {
  position: relative;
  padding-left: 36px;
  margin-bottom: 14px;
}

.ld-timeline-item:last-child {
  margin-bottom: 0;
}

.ld-dot {
  position: absolute;
  left: 8px;
  top: 6px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 0 0 2px #e2e8f0;
}

.ld-dot-primary { background: #4f46e5; }
.ld-dot-success { background: #10b981; }
.ld-dot-danger { background: #ef4444; }

.ld-timeline-card {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.ld-timeline-card strong {
  display: block;
  font-size: 0.9rem;
  color: #0f172a;
  margin-bottom: 2px;
}

.ld-timeline-card span {
  display: block;
  font-size: 0.82rem;
  color: #64748b;
}

.ld-timeline-card small {
  display: block;
  margin-top: 4px;
  font-size: 0.78rem;
  color: #94a3b8;
}

@media (max-width: 640px) {
  .ld-bento,
  .ld-info-grid {
    grid-template-columns: 1fr;
  }

  .ld-hero-stats {
    width: 100%;
  }

  .ld-stat {
    flex: 1;
  }
}
</style>
